import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logout } from "@/lib/auth";
import { getProjects, deleteProject } from "@/lib/portfolio-store";
import { Plus, Edit2, LogOut, Eye, Users, KeyRound } from "lucide-react";

const AdminPanel = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    setProjects(getProjects());
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const handleDelete = (id) => {
    if (deleteConfirm === id) {
      deleteProject(id);
      setProjects(getProjects());
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      Site: "#ff6b35",
      "E-commerce": "#0a0a0a",
      Sistema: "#ff6b35",
      App: "#0a0a0a",
    };
    return colors[cat] || "#ff6b35";
  };

  return (
    <div className="min-h-screen bg-[#f8f5ee]">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b-4 border-[#ff6b35] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl text-white">
              BEZIER<span className="text-[#ff6b35]">CLICK</span>
            </h1>
            <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mt-1">
              Painel de Administração
            </p>
          </div>

          <div className="flex items-center gap-4">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="btn-brutal !p-2 !px-3 text-sm flex items-center gap-2"
            >
              <Eye size={16} />
              Ver Site
            </a>
            <button
              onClick={() => navigate("/admin/users")}
              className="btn-brutal !p-2 !px-3 text-sm flex items-center gap-2"
            >
              <Users size={16} />
              Utilizadores
            </button>
            <button
              onClick={() => navigate("/admin/change-password")}
              className="btn-brutal !p-2 !px-3 text-sm flex items-center gap-2"
            >
              <KeyRound size={16} />
              Senha
            </button>
            <button
              onClick={handleLogout}
              className="btn-brutal btn-brutal-orange !p-2 !px-3 text-sm flex items-center gap-2"
            >
              <LogOut size={16} />
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="font-display text-4xl text-[#0a0a0a]">Portfólio</h2>
            <p className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]/70 mt-2">
              Total: {projects.length} projetos
            </p>
          </div>

          <button
            onClick={() => navigate("/admin/new")}
            className="btn-brutal btn-brutal-orange flex items-center gap-2"
          >
            <Plus size={20} />
            Novo Projeto
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-display text-2xl text-[#0a0a0a]/50 mb-6">
              Nenhum projeto ainda
            </p>
            <button
              onClick={() => navigate("/admin/new")}
              className="btn-brutal btn-brutal-orange"
            >
              Criar Primeiro Projeto
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-white border-4 border-[#0a0a0a] overflow-hidden hover:shadow-brutal transition-shadow"
              >
                {/* Imagem */}
                <div className="relative h-48 overflow-hidden bg-[#0a0a0a]">
                  <img
                    src={project.img}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Conteúdo */}
                <div className="p-5 border-t-4 border-[#0a0a0a]">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display text-lg text-[#0a0a0a] leading-tight">
                      {project.title}
                    </h3>
                    <span
                      className="px-2 py-1 text-xs font-mono uppercase tracking-widest text-white"
                      style={{ backgroundColor: getCategoryColor(project.cat) }}
                    >
                      {project.cat}
                    </span>
                  </div>

                  <p className="text-sm text-[#0a0a0a]/70 mb-4 line-clamp-2">
                    {project.description}
                  </p>

                  {project.tags && project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-5">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs bg-[#ff6b35] text-[#0a0a0a] px-2 py-1 font-mono"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Ações */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/edit/${project.id}`)}
                      className="flex-1 btn-brutal !p-2 flex items-center justify-center gap-2 text-sm"
                    >
                      <Edit2 size={16} />
                      Editar
                    </button>

                    <button
                      onClick={() => handleDelete(project.id)}
                      className={`flex-1 text-sm font-display uppercase border-4 font-bold p-2 transition-colors ${
                        deleteConfirm === project.id
                          ? "bg-red-500 text-white border-red-700"
                          : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
                      }`}
                    >
                      {deleteConfirm === project.id ? "Confirmar?" : "Apagar"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
